import React, { useMemo, useState } from "react";
import { Card, Button, Flex, ConfigProvider } from "antd";
import { PlusOutlined, ShareAltOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { RutasDidacticasList } from "../../../components/Admin/RutasDidacticasComp";
import { SalasList } from "../../../components/Admin/SalasCmp";
import { SharedRoutesModal } from "../RutasDidacticasComp/SharedRoutesModal";
import { PageHeader, useBrandTheme } from "../../../ui";
import { useThemeRuntime } from "../../../ui/theme/ThemeRuntimeProvider";
import { NEW_SALAS } from "../../../ui/featureFlags";

import paso1 from "../../../assets/illustrations/guia-catalogo/paso1.png";
import paso2 from "../../../assets/illustrations/guia-catalogo/paso2.png";
import paso3 from "../../../assets/illustrations/guia-catalogo/paso3.png";

import styles from "./TeacherSalasGrid.module.scss";

export function TeacherSalasGrid(props) {
  const {
    teacher,
    selectedClass,
    accessToken,
    page,
    setPage,
    reload,
    onReload,
    onNewRoute,
  } = props;

  const [showSharedModal, setShowSharedModal] = useState(false);
  const { t } = useTranslation("scenes");

  // Overrides del editor de estilos (guia rapida por pagina). En la sesion de un
  // diseñador ganan; para el resto no hay override => valor por defecto (i18n).
  const { resolvedOverrides } = useThemeRuntime();
  const ov = (key, fallback) => {
    const v = resolvedOverrides[key];
    return typeof v === "string" && v.trim() ? v : fallback;
  };

  const headerSteps = useMemo(
    () => [
      {
        title: ov("text.salas.guide.step1.title", t("catalog.step1Title")),
        description: ov("text.salas.guide.step1.desc", t("catalog.step1Desc")),
        illustration: ov("image.salas.guide.step1", paso1),
      },
      {
        title: ov("text.salas.guide.step2.title", t("catalog.step2Title")),
        description: ov("text.salas.guide.step2.desc", t("catalog.step2Desc")),
        illustration: ov("image.salas.guide.step2", paso2),
      },
      {
        title: ov("text.salas.guide.step3.title", t("catalog.step3Title")),
        description: ov("text.salas.guide.step3.desc", t("catalog.step3Desc")),
        illustration: ov("image.salas.guide.step3", paso3),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, resolvedOverrides],
  );

  const guideTitleOv = ov("text.salas.guide.title", undefined);

  const brandTheme = useBrandTheme();

  const content = (
    <div className={styles.wrap}>
      <PageHeader
        title={t("catalog.pageTitle")}
        description={t("catalog.pageDesc")}
        steps={headerSteps}
        guideTitle={guideTitleOv}
        extra={
          <Flex gap={10} wrap>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              className={NEW_SALAS ? undefined : styles.primaryAction}
              onClick={onNewRoute}
            >
              {t("catalog.newItinerary")}
            </Button>

            <Button
              size="large"
              icon={<ShareAltOutlined />}
              className={styles.secondaryAction}
              onClick={() => setShowSharedModal(true)}
            >
              {t("catalog.sharedItineraries")}
            </Button>
          </Flex>
        }
      />

      <div className={styles.columns}>
        <Card
          className={styles.leftCard}
          variant="outlined"
          styles={{ body: { padding: 16 } }}
        >
          <SalasList
            classNumber={selectedClass}
            teacher={teacher}
            currentPage={page}
            onPageChange={setPage}
            onTotalPagesChange={() => {}}
            reload={reload}
            onReload={onReload}
          />
        </Card>

        <Card
          className={styles.rightCard}
          variant="outlined"
          styles={{ body: { padding: 16 } }}
        >
          <RutasDidacticasList
            teacherId={teacher?._id}
            teacher={teacher}
            classNumber={selectedClass}
            accessToken={accessToken}
            reload={reload}
          />
        </Card>
      </div>

      <SharedRoutesModal
        open={showSharedModal}
        onClose={() => setShowSharedModal(false)}
        teacher={teacher}
        accessToken={accessToken}
        onAdopted={() => {
          onReload?.();
        }}
      />
    </div>
  );

  if (NEW_SALAS) {
    return <ConfigProvider theme={brandTheme}>{content}</ConfigProvider>;
  }

  return content;
}
